/**
 * SearchResultsDropdown Component
 * 
 * Dropdown для отображения результатов поиска тегов
 * Поддерживает клавиатурную навигацию и показ опции "Создать новый тег"
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TagChip } from './TagChip';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import type { Tag } from '@/integrations/supabase/types';
import { useTranslation } from "react-i18next";

interface TagWithUsage extends Tag {
  usage_count?: number;
}

interface SearchResultsDropdownProps {
  searchQuery: string;
  searchResults: TagWithUsage[];
  isVisible: boolean;
  onSelectTag: (tag: Tag) => void;
  onCreateTag?: (name: string) => void;
  isLoading?: boolean;
  maxResults?: number;
  className?: string;
}

export const SearchResultsDropdown: React.FC<SearchResultsDropdownProps> = ({
  searchQuery,
  searchResults,
  isVisible,
  onSelectTag,
  onCreateTag,
  isLoading = false,
  maxResults = 10,
  className = ""
}) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1);
  }, [searchResults, searchQuery]);

  const showCreateOption = onCreateTag && searchQuery.trim().length > 0 &&
    !searchResults.some(tag => tag.name.toLowerCase() === searchQuery.toLowerCase());

  const displayedResults = searchResults.slice(0, maxResults);

  const handleSelectItem = useCallback((index: number) => {
    if (index < displayedResults.length) {
      onSelectTag(displayedResults[index]);
    } else if (showCreateOption && onCreateTag) {
      onCreateTag(searchQuery.trim());
    }
  }, [displayedResults, onSelectTag, showCreateOption, onCreateTag, searchQuery]);

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible) return;

      const totalItems = Math.min(searchResults.length, maxResults) + (showCreateOption ? 1 : 0);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex(prev => (prev + 1) % totalItems);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (activeIndex >= 0) {
            handleSelectItem(activeIndex);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setActiveIndex(-1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, searchResults.length, activeIndex, maxResults, showCreateOption, handleSelectItem]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [activeIndex]);

  if (!isVisible || (!isLoading && searchResults.length === 0 && !showCreateOption)) {
    return null;
  }

  const handleMouseEnter = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <div
      ref={dropdownRef}
      className={`absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto ${className}`}
      data-testid="search-results-dropdown"
    >
      {isLoading ? (
        <div className="p-3 text-center text-sm text-muted-foreground">
          {t('records.tags_search_placeholder')}
        </div>
      ) : (
        <>
          {/* Search results */}
          {displayedResults.map((tag, index) => (
            <button
              key={tag.id}
              ref={(el) => { itemRefs.current[index] = el; }}
              className={`w-full p-2 text-left hover:bg-muted transition-colors ${activeIndex === index ? 'bg-muted' : ''
                }`}
              onClick={() => onSelectTag(tag)}
              onMouseEnter={() => handleMouseEnter(index)}
              data-testid={`search-result-${index}`}
            >
              <div className="flex items-center gap-2">
                <TagChip tag={tag} size="sm" />
                {tag.usage_count && (
                  <span className="text-xs text-muted-foreground">
                    {t('records.tag_usage_count', { count: tag.usage_count })}
                  </span>
                )}
              </div>
            </button>
          ))}

          {/* Create new tag option */}
          {showCreateOption && (
            <button
              ref={(el) => { itemRefs.current[displayedResults.length] = el; }}
              className={`w-full p-2 text-left hover:bg-muted transition-colors border-t border-border ${activeIndex === displayedResults.length ? 'bg-muted' : ''
                }`}
              onClick={() => onCreateTag!(searchQuery.trim())}
              onMouseEnter={() => handleMouseEnter(displayedResults.length)}
              data-testid="create-tag-option"
            >
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  {t('records.create_tag_prefix')} "{searchQuery}"
                </span>
              </div>
            </button>
          )}

          {/* No results message */}
          {!isLoading && displayedResults.length === 0 && !showCreateOption && (
            <div className="p-3 text-center text-sm text-muted-foreground">
              {t('records.tags_not_found')}
            </div>
          )}

          {/* Results count info */}
          {searchResults.length > maxResults && (
            <div className="p-2 text-xs text-muted-foreground text-center border-t border-border">
              {t('records.tags_shown_count', { count: maxResults, total: searchResults.length })}
            </div>
          )}
        </>
      )}
    </div>
  );
};